/**
 * Is the child in the middle of something a reload would ruin?
 *
 * A new service worker takes over as soon as it activates (vite.config.ts `autoUpdate`,
 * `skipWaiting`, `clientsClaim`), and the page then reloads once so the running JS matches the
 * build the worker serves. That reload must never land inside a brushing ritual, a lesson or a
 * game round: two minutes of brushing or a half finished round would be gone, and the child
 * would see the screen jump for no reason they could name.
 *
 * Two things mark the screen as busy. The ritual and the game host take a named hold, because
 * they own state that no route can see. Everything else is the route: the lesson, quiz and
 * chest screens belong to another lane, so their paths are listed here instead of each screen
 * taking its own hold.
 *
 * When the page is busy the reload waits, and the next route change runs it: leaving the
 * ritual or the round is exactly the safe moment.
 */

/** One unbroken run for the child. `notePath` is called from the app shell on every move. */
const BUSY_PATHS = [/^\/firca(\/|$)/, /^\/ders(\/|$)/, /^\/soru(\/|$)/, /^\/sandik(\/|$)/]

const holds = new Set<string>()
let path = '/'
let pending: (() => void) | null = null

export function isBusy(): boolean {
  return holds.size > 0 || BUSY_PATHS.some((pattern) => pattern.test(path))
}

/**
 * Marks the screen busy until the returned release is called. Calling it twice with the same
 * key is one hold, so a remount cannot leave the page busy forever.
 */
export function holdBusy(key: string): () => void {
  holds.add(key)
  return () => {
    holds.delete(key)
    flush()
  }
}

/** The app shell reports every route change here, which is also when a waiting reload runs. */
export function notePath(next: string): void {
  path = next
  flush()
}

/** Runs `apply` as soon as nothing is in flight. A second call replaces the waiting one. */
export function reloadWhenIdle(apply: () => void): void {
  pending = apply
  flush()
}

function flush(): void {
  const apply = pending
  if (!apply || isBusy()) return
  pending = null
  apply()
}

/** Tests only: the module is a singleton, so each case starts from an empty page. */
export function resetBusy(): void {
  holds.clear()
  path = '/'
  pending = null
}
